// Which card, on which platform (migration 073; Mitch, 2026-11-03: "If it was paid with a card, which card, on which
// platform"): the two fields every way of recording a payment made outside HHL Ops shows (Mark paid outside, a run
// cost paid outside, a paper PO's payment). The accounts are Settings → Payment accounts; a card payment must say both.
import { PAID_VIA, PAID_VIA_LABELS, type PaidVia, payAccountText } from "../../../../shared/pay-accounts.ts";
import { Link } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Select } from "../../ui/controls.tsx";

/** The account kinds that fit a method: a card payment by a card, a wire from a bank account, Wise from Wise. */
const KINDS_FOR: Record<string, string[] | null> = { card: ["card"], wire: ["bank"], wise: ["wise"] };
/** Where it can have been paid, by method: a card on a website or a marketplace, a wire through the bank. */
const VIA_FOR: Record<string, readonly PaidVia[]> = {
  card: ["vendor_site", "paypal", "alibaba", "aliexpress", "amazon", "other"],
  wire: ["bank"],
  wise: ["wise"],
};

export function PaidWith({
  method,
  accountId,
  via,
  onChange,
  className,
}: {
  method: string;
  accountId: string | null;
  via: string;
  onChange: (patch: { accountId?: string | null; via?: string }) => void;
  className?: string;
}) {
  const accounts = useRows("pay_account");
  const kinds = KINDS_FOR[method] ?? null;
  const fits = accounts.filter((a) => (a.active || a.id === accountId) && (!kinds || kinds.includes(a.kind)));
  const vias = VIA_FOR[method] ?? PAID_VIA;
  const card = method === "card";
  return (
    <div className={className ?? "grid grid-cols-2 gap-2"}>
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="text-muted">{card ? "Which card" : "Paid from"}</span>
        <Select
          aria-label={card ? "Which card" : "Paid from"}
          value={accountId ?? ""}
          onChange={(e) => onChange({ accountId: e.target.value || null })}
          options={[
            { value: "", label: card ? "Pick the card…" : "Not said" },
            ...fits.map((a) => ({ value: a.id, label: payAccountText(a) + (a.active ? "" : " (not in use)") })),
          ]}
        />
        {!fits.length && (
          <span className="text-[11px] text-faint">
            None yet:{" "}
            <Link to="/settings/payment-accounts" className="underline">
              add {card ? "the card" : "one"} in Settings → Payment accounts
            </Link>
          </span>
        )}
      </label>
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="text-muted">Where</span>
        <Select
          aria-label="Where it was paid"
          value={via}
          onChange={(e) => onChange({ via: e.target.value })}
          options={[{ value: "", label: card ? "Pick where…" : "Not said" }, ...vias.map((v) => ({ value: v, label: PAID_VIA_LABELS[v] }))]}
        />
      </label>
    </div>
  );
}
